import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthConfiguration } from './authentication.interface';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public authConfig?: AuthConfiguration;
  public isLoggedIn = new BehaviorSubject<boolean | undefined>(undefined);
  private activeSessionTimer!: Subscription;
  public activeSessionIsValid = new BehaviorSubject<boolean | undefined>(
    undefined,
  );
  public activeSessionRemainingTime = new BehaviorSubject<number | undefined>(
    undefined,
  );
  public OnErrorCatcher = new BehaviorSubject<any>(null);

  constructor(
    private http: HttpClient,
    public injector: Injector,
    private router: Router,
    @Inject('authConfiguration')
    private readonly authConfiguration: AuthConfiguration,
  ) {
    // Grab Authentication Environment Variables
    this.authConfig = authConfiguration;
  }

  // Initialize MSAL Service
  geMsalService() {
    const msal = this.injector.get(MsalService);
    return msal;
  }

  /**
   * Create  msal Instance
   * This configuration will be used for MSALInstanceFactory
   * */
  getAuthConfig(): any {
    if (!this.authConfig) {
      throw new Error('AuthConfiguration is not set');
    }
    const result = {
      clientId: this.authConfig.clientId,
      authority: this.authConfig.authority,
      navigateToLoginRequestUrl: true,
      redirectUri: this.authConfig.redirectUri,
      postLogoutRedirectUri: this.authConfig.postLogoutRedirectUri,
    };
    return result;
  }

  /**
   * Get active logged-in account
   * If no active account set but there are accounts signed in, sets first account to active account
   * To use active account set here, subscribe to inProgress$ first in your component
   * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
   */
  getAccount() {
    const msal = this.geMsalService();
    let activeAccount = msal.instance.getActiveAccount();
    if (!activeAccount && msal.instance.getAllAccounts().length > 0) {
      const accounts = msal.instance.getAllAccounts();
      msal.instance.setActiveAccount(accounts[0]);
      activeAccount = msal.instance.getActiveAccount();
    }
    return activeAccount;
  }

  /**
   * Login Redirect
   * */
  login() {
    const msal = this.geMsalService();
    //msal.loginRedirect();
    msal.loginRedirect({
      scopes: [environment?.authConfig?.scope],
      redirectStartPage:
        environment?.authConfig?.postLogoutRedirectUri + '?status=in-progress',
    });
  }

  /**
   * logout will clear the browser session and storage
   * and set local logout status
   */
  logout() {
    const msal = this.geMsalService();
    localStorage.clear();
    msal.logoutRedirect();
  }

  /**
   * Attempt to renew the token silently
   * If silent token renewal fails, redirect to login
   */
  renewToken() {
    const msal = this.geMsalService();
    console.log("tratando de renovar token");
    msal.instance.acquireTokenSilent({
      scopes: [environment?.authConfig?.scope],
    }).then((response) => {
      // Token acquired silently, reset session timer
      this.setActiveSessionTimer();
    }).catch((error) => {
      if (error instanceof InteractionRequiredAuthError) {
        // Token renewal requires user interaction, redirect to login
        this.login();
      } else {
        console.log('Token renewal failed:', error);
      }
    });
  }

  /**
   * Set Active Session Timer for user session expiry warning
   * */
  setActiveSessionTimer() {
    const inSecond = 1000;
    const defaultActiveSessionTime = 1800; // in seconds
    const defaultWarningTime = 60; // in seconds
    const activeSessionTime = this.authConfig?.activeSessionTimeOutInSeconds
      ? this.authConfig.activeSessionTimeOutInSeconds
      : defaultActiveSessionTime; // in seconds
    let warningTime = this.authConfig?.activeSessionTimeOutWarningInSeconds
      ? this.authConfig.activeSessionTimeOutWarningInSeconds
      : defaultWarningTime; // in seconds
    this.activeSessionIsValid.next(true);

    // Clear any existing timer
    if (this.activeSessionTimer) {
      this.activeSessionTimer.unsubscribe();
    }

    // Start a new timer
    this.activeSessionTimer = timer(
      activeSessionTime * inSecond,
      inSecond,
    ).subscribe((time) => {
      const remainingTime = warningTime - time;
      this.activeSessionRemainingTime.next(remainingTime);
      console.log(remainingTime);
      if (0 >= remainingTime) {
        this.renewToken();
        // Redirect to session expired route
        //if (this.authConfig?.sessionExpiredRoutePath) {
        //  this.router.navigate([this.authConfig?.sessionExpiredRoutePath]);
        //}
        // Active Session Timer Expired
        this.activeSessionIsValid.next(false);
        this.activeSessionTimer.unsubscribe();
      }
    });
  }

  // Renew active session validity
  renewActiveSession() {
    const renewSessionApiUrl = this.authConfig?.renewSessionApiUrl;
    if (renewSessionApiUrl) {
      this.http.get(renewSessionApiUrl).subscribe(
        () => {
          this.setActiveSessionTimer();
        },
        () => {
          this.activeSessionIsValid.next(false);
        },
      );
    } else {
      this.setActiveSessionTimer();
    }
    return this.activeSessionIsValid;
  }

  /**
   * Register msal broadcasted hooks
   *
   * */
  registerMsalBroadcast() {
    const msal = this.geMsalService();
    const msalBroadcastService = this.injector.get(MsalBroadcastService);
    // Enable logging of MSAL events for easier troubleshooting
    msal.instance.enableAccountStorageEvents();
    const isLoggedIn = msal.instance.getAllAccounts().length > 0;
    //console.log("Cuentas", msal.instance.getAllAccounts());
    this.isLoggedIn.next(isLoggedIn);
    if (isLoggedIn) {
      this.setActiveSessionTimer();
    }

    // Subscribe to msal broadcast events
    msalBroadcastService.msalSubject$.subscribe(
      (payload: any) => {
        switch (payload.eventType) {
          // MSAL Login Success
          case EventType.LOGIN_SUCCESS:
            this.isLoggedIn.next(true);
            this.setActiveSessionTimer();
            break;

          // MSAL Logout Success
          case EventType.LOGOUT_SUCCESS:
            this.isLoggedIn.next(false);
            this.activeSessionIsValid.next(false);
            break;

          // Acquire Token Failure
          case EventType.ACQUIRE_TOKEN_FAILURE:
            this.isLoggedIn.next(false);
            this.activeSessionIsValid.next(false);
            break;
        }
      },
      (err) => console.log("error", err),
    );
  }
}
